import { useState, useEffect, useCallback, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  ReMixChart,
  lightenColor,
} from "../../../components/recharts/ReMixCharts";

import { RajasthanDiscomData, RajGenProfile } from "../../../models/rajasthan";
import {
  getRajasthanForecastDiscomData,
  getRajGenProfileData,
  getSurDefProfile,
} from "../../Rajasthan/Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { R_CHART_TYPES } from "../const";
import { FUNCTIONS } from "../const_raj";
import { convertToString } from "../../../extensions/date";

import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import Tooltip from "../../../components/tooltip";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { BrushStart } from "../../../models/chart_model";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import RajasthanForecastPeakAnalysis from "./Forecast_SurplusPeak";
import RajasthanForecastSummaryAnalysis from "./Forecast_SurplusSummary";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { getRajasthanSurplusMetrics } from "../../Rajasthan/Utility/RestApi";
import { SurplusDeficitData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";

// Grouping and filtering configurations
const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];

const DISCOM = [
  {
    label: "Discom 1",
    key: "DISCOM1",
  },
  {
    label: "Discom 2",
    key: "DISCOM2",
  },
  {
    label: "Discom 3",
    key: "DISCOM3",
  },
];

// ─── DEMO MASKING ────────────────────────────────────────────────────────────
// Helper: assigns "Power plant N" labels to every entry whose stackId === "gen"
// so real plant names never appear anywhere (chart, tooltip, table, CSV).
function maskGenLabels(
  keys: { key: string; label: string; stackId: string; yAxis?: string }[],
) {
  let count = 0;
  return keys.map((k) => ({
    ...k,
    label: k.stackId === "gen" ? `Power plant ${++count}` : k.label,
  }));
}
// ─────────────────────────────────────────────────────────────────────────────

const GEN_BY = [
  {
    label: "Cumulative",
    keys: maskGenLabels([
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      {
        key: "total",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
    ]),
  },
  {
    label: "Comparative",
    keys: maskGenLabels([
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      { key: "sgu", label: "SGU", stackId: "gen" },
      { key: "biomass", label: "Biomass", stackId: "gen" },
      { key: "hydro", label: "Hydro", stackId: "gen" },
      { key: "solar", label: "Solar", stackId: "gen" },
      { key: "wind", label: "Wind", stackId: "gen" },
      { key: "isgs", label: "ISGS", stackId: "gen" },
      { key: "lta", label: "LTA", stackId: "gen" },
      { key: "ldc", label: "LDC", stackId: "gen" },
      {
        key: "kalisindhtpsstage1_unit1",
        label: "kalisindhtpsstage1 unit1",
        stackId: "gen",
      },
      { key: "adanikawai", label: "Adani Kawai", stackId: "gen" },
      { key: "barsingsarneyveli", label: "Barsingsar Neyveli", stackId: "gen" },
      {
        key: "chhabratpsstage1_unit12",
        label: "Chhabra TPS Stage1 Unit1/2",
        stackId: "gen",
      },
      { key: "csctpp", label: "CSCTPP", stackId: "gen" },
      {
        key: "dholpurgtpp_unit123",
        label: "Dholpur GTPP Unit1/2/3",
        stackId: "gen",
      },
      { key: "giral", label: "Giral", stackId: "gen" },
      { key: "ktpsstage12345", label: "KTPS Stage1/2/3/4/5", stackId: "gen" },
      { key: "rajwest_unit8", label: "Rajwest Unit8", stackId: "gen" },
      {
        key: "ramgarhgtppstage123",
        label: "Ramgarh GTPP Stage1/2/3",
        stackId: "gen",
      },
      { key: "rappa", label: "RAPPA", stackId: "gen" },
      { key: "ssctps_6", label: "SSCTPS 6", stackId: "gen" },
      { key: "stpsstage1234", label: "STPS Stage1/2/3/4", stackId: "gen" },
      { key: "vslignite", label: "VS Lignite", stackId: "gen" },
    ]),
  },
];

const DRAWAL_BY = [
  {
    label: "Cumulative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
  {
    label: "Comparative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
];

// X_AXIS uses time_block (which exists in payload) and date
const X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Discom/ Area",
    key: "area",
    isTime: false,
    values: ["area", "discom"],
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const CONTENT_TYPE = ["Chart", "Table"];

const MODELS = ["Model 1", "Model 2"];

export function Rajasthan_Forecast_Surplus() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [genData, setGenData] = useState<RajGenProfile[]>([]);
  const [genGroupBy, setGenGroupBy] = useState(GEN_BY[0]);
  const [drawalGroupBy, setDrawalGroupBy] = useState(DRAWAL_BY[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE[0],
  );
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SurplusDeficitData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const newData = await getRajasthanForecastDiscomData(
      convertToString(dates.startDate),
      convertToString(dates.endDate),
      selectedModel,
    );
    const genData = await getSurDefProfile({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    setData(newData);
    setGenData(genData);

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        ),
      ),
    );
    const uniqueTimeBlocks = Array.from(
      new Set(newData.map((item: any) => item.time_block)),
    ).sort((a: any, b: any) => Number(a) - Number(b));
    setUniqueBlock(uniqueTimeBlocks);
    setLoading(false);
  }, [activeTab, DefaultDates, selectedModel]);

  useEffect(() => {
    let filteredData = data;

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom),
      );
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedBlock
          .map((b: any) => (typeof b === "object" ? b?.value : b))
          .includes(item.time_block),
      );
    }

    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate,
    );

    const determineKeys = () => {
      if (selectedXaxis.isTime) {
        return ["time_block", "date"];
      }
      return selectedGroupKeys[0].values;
    };

    const par: ChartDataParams = {
      keys: determineKeys(),
      groupBy: selectedXaxis.isTime ? selectedGroupKeys.map((k) => k.key) : [],
      tableKeys: ["date", "time_block", "month"],
      volumeKey: "forecasted",
      aggregationKeys: ["forecasted"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    const drawal = cData.data.map((item) => ({
      ...item,
      Total_drawal_mw: item.forecasted,
    }));

    function cleanOnbardcKeys(obj: Record<string, any>) {
      const cleaned: Record<string, any> = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (key.startsWith("onbardc_")) {
          const newKey = key.replace(/^onbardc_/, "");
          cleaned[newKey] = value;
        } else {
          cleaned[key] = value;
        }
      });
      return cleaned;
    }

    const final_raw_data = genData.map(cleanOnbardcKeys);

    let filteredGenData = final_raw_data.filter((item: any) => {
      const itemDate = new Date(item.date);
      return itemDate >= dates.startDate && itemDate <= dates.endDate;
    });

    if (selectedDate.length > 0) {
      filteredGenData = filteredGenData.filter((item: any) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    if (selectedBlock.length > 0) {
      filteredGenData = filteredGenData.filter((item: any) =>
        selectedBlock
          .map((b: any) => (typeof b === "object" ? b?.value : b))
          .includes(item.time_block),
      );
    }

    const chartPar: ChartDataParams = {
      keys: ["date", "time_block"],
      tableKeys: ["date", "time_block"],
      groupBy: [],
      aggregationKeys: [
        "forecasted",
        "total",
        "sgu",
        "biomass",
        "hydro",
        "solar",
        "wind",
        "isgs",
        "lta",
        "ldc",
        "kalisindhtpsstage1_unit1",
        "adanikawai",
        "barsingsarneyveli",
        "chhabratpsstage1_unit12",
        "csctpp",
        "dholpurgtpp_unit123",
        "giral",
        "ktpsstage12345",
        "rajwest_unit8",
        "ramgarhgtppstage123",
        "rappa",
        "ssctps_6",
        "stpsstage1234",
        "vslignite",
      ],
      priceKey: "",
      volumeKey: "",
      data: filteredGenData,
    };

    const res_data = FormatChartData(chartPar);

    const final_data = res_data.data.map((item: any) => {
      const deficit = item._total - item._forecasted;
      const deficit_percent =
        item._forecasted !== 0 ? (deficit / item._forecasted) * 100 : 0;
      return {
        ...item,
        _deficit: deficit,
        _deficit_percent: deficit_percent,
      };
    });

    const finalTableData = res_data.data.map((item: any) => {
      const deficit = item._total - item._forecasted;
      const deficit_percent =
        item._forecasted !== 0 ? (deficit / item._forecasted) * 100 : 0;
      return {
        ...item,
        _deficit: deficit,
        _deficit_percent: deficit_percent,
      };
    });

    const sortedChartData = final_data.sort((a: any, b: any) => {
      if (a.date && b.date) {
        const dateComparison =
          new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateComparison !== 0) return dateComparison;
      }
      if (a.time_block !== undefined && b.time_block !== undefined) {
        return Number(a.time_block) - Number(b.time_block);
      }
      return 0;
    });

    setChartData(sortedChartData);
    setChartKeys(cData.keys);
    setTableData(finalTableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
    selectedContentType,
    genData,
    DefaultDates,
    activeTab,
  ]);

  const getXDataKey = () => {
    if (selectedXaxis.isTime) {
      return "time_block";
    }
    return selectedGroupKeys[0].values[0];
  };

  const getSecondXDataKey = () => {
    if (selectedXaxis.isTime) {
      return "date";
    }
    return undefined;
  };

  // Since GEN_BY labels are already masked at source via maskGenLabels(),
  // key.label is always safe to use directly — no further masking needed here.
  const allLegends = useMemo(() => {
    return drawalGroupBy.keys
      .concat(genGroupBy.keys)
      .reverse()
      .map((key) => ({
        dataKey: "_" + key.key,
        name: key.label, // already masked at source
      }));
  }, [drawalGroupBy.keys, genGroupBy.keys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(allLegends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    tableData: TableData,
    filename: AddDatesWithFileName({
      fileName: "Forecast Discom Load",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
    data: chartData,
    unit: "MW",
    extraKeys: ["time_block", "date"],
    extraLabels: ["Block", "Date"],
  };

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = convertToString(dates.startDate);
      const endDateStr = convertToString(dates.endDate);

      const metricsData = await getRajasthanSurplusMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block: any) => block.value),
          },
        ],
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleModelChange(model: string) {
    setSelectedModel(model);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleGenGroupByChange(index: number) {
    setGenGroupBy(GEN_BY[index]);
  }

  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanForecastPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        genGroupBy={genGroupBy}
        onGenGroupByChange={handleGenGroupByChange}
        metrics={metrics}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanForecastSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        genGroupBy={genGroupBy}
        onGenGroupByChange={handleGenGroupByChange}
        metrics={metrics}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => {
              setGenGroupBy(GEN_BY[index]);
            }}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-2 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Surplus/Deficit"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
            ]}
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Total Generation"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.unit ??
                  "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.unit ??
                  "",
              },
            ]}
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title="Load"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.load_metrics?.["Max Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Max Load (MW)"]?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.load_metrics?.["Min Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Min Load (MW)"]?.unit ?? "",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Surplus/Deficit Analysis"
              subtitle="Fetching Demo State forecast surplus data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading">Surplus/Deficit Analysis (MW)</h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item: any) =>
                      typeof item === "object" ? item?.value : item,
                    )}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV(tablePar);
                      }
                      if (dataTab.label === "Summary") {
                        downloadSummaryCSV({ ...tablePar });
                      }
                      if (dataTab.label === "Table") {
                        downloadChartTableCSV(tablePar);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedXaxis.isTime}
                  onlyTitle={selectedBlock.length > 0}
                  barCategoryGap={10}
                  unit="MW"
                  yAxisLabel="MW"
                  secondYAxisLabel="%"
                  legends={allLegends.map((leg) => ({
                    ...leg,
                    legendColor: undefined,
                    type: selectedChartType.value,
                    yAxisId: undefined,
                    stackId: selectedChartType.stackId ?? undefined,
                  }))}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={getXDataKey()}
                  secondXDataKey={getSecondXDataKey()}
                  xTick={
                    selectedBlock.length === 0
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={
                <p>Forecast of Total Drawal and Generation + Procurement</p>
              }
              text="1. Surplus/Deficit = Total Gen. + Proc. - Total Drawal
       2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
